import type { SectionConfig } from '#types/config/components/cms/section'
import type { Content } from '#types/content'

const getContentPaddings = (componentSidePaddings: NonNullable<Content['componentSidePaddings']>, config: SectionConfig): string => {
  const { componentSidePaddingMap, componentBoxSizing } = config

  const paddings: string[] = Object.entries(componentSidePaddings)
    .filter(([_, v]) => v)
    .map(([k]) => componentSidePaddingMap[k])

  if (!paddings.length) return ''

  const result: string[] = []
  if (componentBoxSizing) result.push(componentBoxSizing)
  result.push(...paddings)

  return result.join(' ')
}

/**
 * Builds the UnoCSS class string that applies the CMS-configured side paddings and max-width to
 * a content section. Returns an empty string when neither padding nor max-width is configured.
 * @param content - The CMS content node (only `componentSidePaddings` and `componentMaxWidth` are read).
 * @param config - Section config providing the paddings/max-width class maps and box-sizing modifier.
 * @param skipAlignment - Optional boolean flag indicating that horizontal alignment classes must be skipped.
 * @returns Space-joined class string ready for `:class` binding.
 * @category Utils
 */
export const getContentInset = (content: Pick<Content, 'componentSidePaddings' | 'componentMaxWidth'>, config: SectionConfig, skipAlignment?: boolean): string => {
  const { componentSidePaddings, componentMaxWidth } = content
  if (!componentSidePaddings && !componentMaxWidth) return ''

  const result: string[] = []

  if (componentSidePaddings) result.push(getContentPaddings(componentSidePaddings, config))

  if (componentMaxWidth) {
    if (!skipAlignment) result.push('mx-a')
    result.push(config.componentMaxWidthMap[componentMaxWidth])
  }

  return result.filter(Boolean).join(' ')
}

/**
 * Returns the padding classes for the **opposite** of the content's `componentSidePaddings`
 * - i.e. the breakpoints where the content does **not** have a side padding receive a padding
 * instead. Used to keep adjacent siblings visually aligned across responsive layouts.
 * @param content - The CMS content node (only `componentSidePaddings` is read).
 * @param config - Section config providing the padding class map.
 * @returns Space-joined class string ready for `:class` binding.
 * @category Utils
 */
export const getInvertedPaddings = (content: Pick<Content, 'componentSidePaddings'>, config: SectionConfig): string => {
  const { componentSidePaddings } = content

  const invertedSidePaddings = mapObject(componentSidePaddings ?? { sm: false, lg: false }, (v) => !v)

  return getContentPaddings(invertedSidePaddings, config)
}
